/**
 * Projects API - TanStack Query hooks for projects
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from './client'
import { QUERY_KEYS } from '@/config/constants'
import type {
  ProjectsResponse,
  ProjectTagsResponse,
  Template,
  TemplatesResponse,
  TemplateContentResponse,
} from '@/types/api.types'

/**
 * Fetch all projects
 */
async function fetchProjects(): Promise<ProjectsResponse> {
  const response = await apiClient.get<ProjectsResponse>('/projects')
  return response.data
}

/**
 * Hook: Get all projects
 */
export function useProjects() {
  return useQuery({
    queryKey: QUERY_KEYS.PROJECTS,
    queryFn: fetchProjects,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  })
}

/**
 * Fetch templates for a project
 */
async function fetchTemplates(projectId: string): Promise<Template[]> {
  const response = await apiClient.get<TemplatesResponse>(`/projects/${projectId}/templates`)
  return response.data.templates
}

/**
 * Hook: Get templates for a project
 */
export function useTemplates(projectId: string, enabled = true) {
  return useQuery({
    queryKey: QUERY_KEYS.TEMPLATES(projectId),
    queryFn: () => fetchTemplates(projectId),
    enabled: enabled && !!projectId,
    staleTime: 10 * 60 * 1000, // 10 minutes
  })
}

/**
 * Fetch project tags
 */
async function fetchProjectTags(projectId: string): Promise<ProjectTagsResponse> {
  const response = await apiClient.get<ProjectTagsResponse>(`/projects/${projectId}/tags`)
  return response.data
}

/**
 * Hook: Get project tags
 */
export function useProjectTags(projectId: string, enabled = true) {
  return useQuery({
    queryKey: QUERY_KEYS.PROJECT_TAGS(projectId),
    queryFn: () => fetchProjectTags(projectId),
    enabled: enabled && !!projectId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Fetch template content
 */
async function fetchTemplateContent(projectId: string, templateName: string): Promise<string> {
  const response = await apiClient.get<TemplateContentResponse>(
    `/projects/${projectId}/templates/${templateName}/content`
  )
  return response.data.template.content
}

/**
 * Hook: Get template content
 */
export function useTemplateContent(projectId: string, templateName: string, enabled = true) {
  return useQuery({
    queryKey: QUERY_KEYS.TEMPLATE_CONTENT(projectId, templateName),
    queryFn: () => fetchTemplateContent(projectId, templateName),
    enabled: enabled && !!projectId && !!templateName,
    staleTime: 30 * 60 * 1000, // 30 minutes (templates rarely change)
  })
}

/**
 * Trigger project reindex
 */
async function reindexProject(projectId: string): Promise<void> {
  await apiClient.post(`/projects/${projectId}/reindex/stream`)
}

/**
 * Hook: Reindex project (mutation)
 */
export function useReindexProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: reindexProject,
    onSuccess: (_, projectId) => {
      // Invalidate project queries after reindex
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROJECT(projectId),
      })
    },
  })
}

/**
 * Get single project details
 */
async function fetchProject(projectId: string): Promise<ProjectsResponse['projects'][0]> {
  const response = await apiClient.get<{
    status: string
    project: ProjectsResponse['projects'][0]
  }>(`/projects/${projectId}`)
  return response.data.project
}

/**
 * Hook: Get single project
 */
export function useProject(projectId: string, enabled = true) {
  return useQuery({
    queryKey: QUERY_KEYS.PROJECT(projectId),
    queryFn: () => fetchProject(projectId),
    enabled: enabled && !!projectId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Create new project
 */
async function createProject(project: unknown): Promise<void> {
  await apiClient.post('/projects', project)
}

/**
 * Hook: Create project (mutation)
 */
export function useCreateProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createProject,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PROJECTS })
    },
  })
}

/**
 * Update existing project
 */
async function updateProject({
  projectId,
  project,
}: {
  projectId: string
  project: unknown
}): Promise<void> {
  await apiClient.put(`/projects/${projectId}`, project)
}

/**
 * Hook: Update project (mutation)
 */
export function useUpdateProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: updateProject,
    onSuccess: (_, { projectId }) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PROJECTS })
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PROJECT(projectId) })
    },
  })
}

/**
 * Delete project
 */
async function deleteProject(projectId: string): Promise<void> {
  await apiClient.delete(`/projects/${projectId}`)
}

/**
 * Hook: Delete project (mutation)
 */
export function useDeleteProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteProject,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PROJECTS })
    },
  })
}
